import { Component } from '@angular/core';
import { isEmpty as _isEmpty, isNil as _isNil } from 'lodash';
import { finalize } from 'rxjs/operators';
import { forkJoin as ObservableForkJoin } from 'rxjs/observable/forkJoin';

import {
  AbstractBlModal,
  ModalButtonType,
  ModalReason,
  ModalSize,
  Rules,
  BlFormGroup,
  BlValidatorService,
  FormGroupOption,
  BlDialogService,
  BlLoadingService,
  ModalButtonSetting,
} from '@blcloud/bl-ng-ui-component';
import { IReceptionInfo, IBizNegoInfo, IVehicleInfo } from '@blcloud/bl-datamodel';
import { BizNegoStatusMap, BizNegoStatus } from '@blcloud/bl-datamodel/enum/deals/biz-nego-status';
import { BizNegoInfoResource } from '@blcloud/bl-ng-resource';
import { BlApiErrorInfo, HttpStatus } from '@blcloud/bl-ng-common';
import { DateTimeUtils, StringUtils } from '@blcloud/bl-common';

import { IReceptStateModalEditData, ICancelReason } from './recept-state-modal.interface';
import { ReceptStateModalService } from './recept-state-modal.service';

/**
 * モーダルの動作モード
 */
export enum ModalMode {
  /** 受付再開モード */
  ResumeReception = 'RESUME_RECEPTION',
  /** 完了取り消しモード */
  CancelCompletion = 'CANCEL_COMPLETION',
  /** 変更モード */
  Change = 'CHANGE',
}

/**
 * ボタン表示テキストの定義
 */
export enum ButtonTitle {
  /** 受付再開ボタン */
  ResumeReception = '受付再開',
  /** 完了取消ボタン */
  CancelCompletion = '完了取消',
  /** 変更ボタン */
  Change = '変更',
}

/**
 * 受付状況モーダル
 */
@Component({
  templateUrl: './recept-state-modal.component.html',
  styleUrls: ['./recept-state-modal.component.scss'],
})
export class ReceptStateModalComponent extends AbstractBlModal {
  readonly BizNegoStatus = BizNegoStatus;
  readonly ModalMode = ModalMode;

  /** 現在のモーダルの動作モード */
  currentMode: ModalMode = ModalMode.Change;

  okButtonSetting: ModalButtonSetting = {
    label: '決定',
  };

  /** 初期表示用の商談ステータス項目を定義 */
  private readonly initialBizNegoStatusItems = [
    {
      code: BizNegoStatus.During,
      name: BizNegoStatusMap[BizNegoStatus.During],
    },
    {
      code: BizNegoStatus.Completion,
      name: BizNegoStatusMap[BizNegoStatus.Completion],
    },
    {
      code: BizNegoStatus.Cancel,
      name: BizNegoStatusMap[BizNegoStatus.Cancel],
    },
  ];

  /** 実際に使用する商談ステータスの配列 */
  bizNegoStatusItems: Array<{ code: string; name: string }> = [];

  /** 商談ステータスに応じてボタンテキスト */
  // buttonText: string;
  defaultCancelReasonCode: number;
  /** 選択組織 */
  _organizationCode: string;

  /** ボタンタイプ設定 */
  footerButtonType: ModalButtonType = ModalButtonType.OkCancel;

  entity: IReceptionInfo;
  editData: IReceptStateModalEditData = {};
  cancelReasonList: ICancelReason[] = [];

  /** 受付情報 */
  receptionInfo: IReceptionInfo;

  /** 商談情報 */
  bizNegoInfo: IBizNegoInfo;
  /** 車両情報 */
  vehicleInfo: IVehicleInfo;

  /** フォームグループ */
  blFormGroup = new BlFormGroup();

  /** フォームグループオプション */
  formGroupOption: FormGroupOption = {
    applyRules: [
      'BizNegoInfo#bizNegoStatus',
      'BizNegoInfo#cancelDate',
      'BizNegoInfo#cancelReasonAnalysisItemCode',
      'BizNegoInfo#cancelReason',
    ],
  };

  /** コンストラクタ */
  constructor(
    private service: ReceptStateModalService,
    private bizNegoInfoResource: BizNegoInfoResource,
    private validatorService: BlValidatorService,
    private dialogService: BlDialogService,

    private loadingService: BlLoadingService
  ) {
    super();
  }

  /** @override */
  defaultOption() {
    return { size: ModalSize.Large };
  }

  getResults(): IReceptStateModalEditData {
    return this.editData;
  }

  /** 初期表示データ取得 */
  onShown() {
    if (this.data) {
      this.editData = this.data;
      // modalに渡されたデータから受付情報と商談情報を保存
      this.receptionInfo = this.data.receptionInfo;
      this.bizNegoInfo = this.data.bizNegoInfo;
      this.vehicleInfo = this.data.vehicleInfo;

      // 商談ステータスに基づいてモードを設定
      this.currentMode = this.determineModalMode(this.editData.bizNegoStatus);

      // ボタンのラベル変更
      this.okButtonSetting = { label: this.getButtonText(this.currentMode) };
    }

    // キャンセル理由の取得
    this.showLoading();
    ObservableForkJoin([this.service.getCancelReasonList(this.data.organizationCode)])
      .pipe(
        finalize(() => {
          this.hideLoading();
        })
      )
      .subscribe(
        ([cancelReasonList]) => {
          this.cancelReasonList = cancelReasonList;
          this.defaultCancelReasonCode = (cancelReasonList.find((v) => v.isDefault) || { code: undefined }).code;
          this.initialize();
        },
        (error) => {
          this.handleError(error);
        }
      );

    // ルールの取得
    this.bizNegoInfoResource.getRules<Rules[]>().subscribe((rules) => {
      this.blFormGroup = this.validatorService.getBlFormGroup(rules, this.formGroupOption);
    });
  }

  onChangeBizNegoStatus(value: string) {
    this.editData.bizNegoStatus = value;
  }

  /**
   * モーダルウィンドウ終了処理
   * @param reason
   */
  dismiss(reason: ModalReason): void {
    if (reason === ModalReason.Done && this.receptionInfo && this.bizNegoInfo) {
      // OKボタンが押された場合は受付情報を更新
      this.loadingService.show();
      this.service
        .putReceptionInfo(this.receptionInfo, this.bizNegoInfo, this.editData, this.vehicleInfo)
        .pipe(finalize(() => this.loadingService.hide()))
        .subscribe(
          () => {
            // 更新成功後にモーダルを閉じる
            this.emitDismissEvent(reason);
          },
          (error) => {
            this.handleError(error, '受付情報の更新に失敗しました。<br>時間をおいて再度お試しください。');
          }
        );
    } else {
      // キャンセルの場合はそのままモーダルを閉じる
      this.emitDismissEvent(reason);
    }
  }

  initialize() {
    // 現在の商談ステータスに基づいて選択肢リストを生成
    this.bizNegoStatusItems = this.getBizNegoStatusItems(this.editData.bizNegoStatus);
    this.editData.cancelDate =
      _isNil(this.editData.cancelDate) || this.editData.cancelDate === DateTimeUtils.initial.iso8601.date
        ? DateTimeUtils.formatIso(DateTimeUtils.today())
        : this.editData.cancelDate;
    this.editData.cancelReasonAnalysisItemCode = !!this.editData.cancelReasonAnalysisItemCode
      ? this.editData.cancelReasonAnalysisItemCode
      : this.defaultCancelReasonCode;
  }

  /**
   * 商談ステータス選択肢を生成する
   *
   * @param currentStatus 現在の商談ステータス
   * @returns 選択肢配列（現在のステータスを先頭に、重複なし）
   */
  private getBizNegoStatusItems(currentStatus: string | null): Array<{ code: string; name: string }> {
    // 1. nullチェック：currentStatusがnull/undefinedの場合、基本ステータスリストをそのまま返す
    if (_isNil(currentStatus)) {
      return this.initialBizNegoStatusItems; // 不変性が保たれている前提でコピーせず返却
    }

    // 2. 基本ステータス選択肢のコピーを作成（元の配列を変更しないため）
    const baseStatusItems = [...this.initialBizNegoStatusItems];

    // 3. 現在のステータス項目を生成
    // BizNegoStatusMapから表示名を取得、存在しない場合は「不明」を表示
    const currentStatusItem = {
      code: currentStatus,
      name: BizNegoStatusMap[currentStatus as keyof typeof BizNegoStatusMap] || '不明',
    };

    // 完了またはキャンセルステータスの場合、現在のステータスのみを返す
    // （これらのステータスでは他の選択肢を許可しないため）
    if (currentStatus === BizNegoStatus.Completion || currentStatus === BizNegoStatus.Cancel) {
      return [currentStatusItem];
    }

    // 4. 基本リストから最初の項目を除去（配列の分割代入を使用）
    // 最初の要素を無視し、残りの要素をremainingBaseItemsとして取得
    const [, ...remainingBaseItems] = baseStatusItems;

    // 5. 現在のステータスを先頭に、残りの基本リストを追加して返す
    // （ユーザーが現在のステータスと他の可能なステータスを選択できるように）
    return [currentStatusItem, ...remainingBaseItems];
  }

  /**
   * 現在のモードに基づいてボタンテキストを取得する
   *
   * @param mode 現在のモーダルモード
   * @returns 表示するボタンテキスト
   */
  private getButtonText(mode: ModalMode): string {
    const modeTextMap = {
      [ModalMode.ResumeReception]: ButtonTitle.ResumeReception,
      [ModalMode.CancelCompletion]: ButtonTitle.CancelCompletion,
      [ModalMode.Change]: ButtonTitle.Change,
    };

    return modeTextMap[mode];
  }

  /**
   * 商談ステータスに基づいてモードを判定する
   *
   * @param bizNegoStatus 現在の商談ステータス
   * @returns 対応するモーダルモード
   */
  private determineModalMode(bizNegoStatus: string | null): ModalMode {
    if (!bizNegoStatus) {
      return ModalMode.Change;
    }

    const statusModeMap = {
      [BizNegoStatus.Completion]: ModalMode.CancelCompletion,
      [BizNegoStatus.Cancel]: ModalMode.ResumeReception,
    };

    return statusModeMap[bizNegoStatus] || ModalMode.Change;
  }

  /**
   * 検索エラーハンドラ
   * @param error エラー情報
   */
  handleError(error: BlApiErrorInfo, message?: string) {
    if (error.status === HttpStatus.Unauthorized || error.status === HttpStatus.Forbidden) {
      // 認証と権限エラーはスロー
      throw error;
    }

    let msg = '';
    if (message) {
      msg = message;
    } else if (error.status === HttpStatus.BadRequest && !_isEmpty(error.messages)) {
      msg = StringUtils.concatWith('\n', ...error.messages);
    }

    this.dialogService.warn(msg).subscribe((dialogRef) => {
      dialogRef.hide();
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/page/recept-list-page/modal/recept-state-modal/recept-state-modal.component.ts